"use client";

import i18n from "@/utils/i18n";
import { I18nextProvider, useTranslation } from "react-i18next";
import { useEffect } from "react";

const VALID_LOCALES = new Set(["ko_KR", "en_US", "ja_JP", "zh_CN"]);

function getLocaleFromCookie(): string | null {
  const localeCookie = document.cookie
    .split(";")
    .find((cookie) => cookie.trim().startsWith("locale="));

  if (!localeCookie) return null;

  const locale = decodeURIComponent(localeCookie.split("=")[1]);
  return VALID_LOCALES.has(locale) ? locale : null;
}

function FontProvider({ children }: { children: React.ReactNode }) {
  const { i18n } = useTranslation();

  useEffect(() => {
    const cookieLocale = getLocaleFromCookie();
    if (cookieLocale && i18n.language !== cookieLocale) {
      void i18n.changeLanguage(cookieLocale);
    }

    const getFontClass = () => {
      switch (i18n.language) {
        case "zh_CN":
          return "font-chinese"; // 중국어 전용 폰트
        case "ja_JP":
          return "font-japanese"; // 일본어 전용 폰트
        case "en_US":
          return "font-english"; // 영어 전용 폰트
        default:
          return "font-pretendard"; // 기본 pretendard 폰트 (한국어)
      }
    };

    const body = document.body;
    // 기존 폰트 클래스 제거
    body.classList.remove("font-pretendard", "font-chinese", "font-japanese", "font-english");
    // 새로운 폰트 클래스 추가
    body.classList.add(getFontClass());
  }, [i18n, i18n.language]);

  return <>{children}</>;
}

export default function I18nProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <I18nextProvider i18n={i18n}>
      <FontProvider>{children}</FontProvider>
    </I18nextProvider>
  );
}
